import { useMemo, useState } from 'react'
import { AutoComplete, Button, Input } from 'antd'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Breadcrumb, Icon } from '@mysaiva/core'
import type { BreadcrumbItem } from '@mysaiva/core/components'
import { useThemeMode } from '../theme/ModeContext'
import { useBreadcrumbOverride } from './useWorkspaceBreadcrumb'
import {
  WORKSPACE_PAGES,
  resolveWorkspaceLocation,
  subPageLabel,
  subPagePath,
  workspacePath
} from '../navigation'
import styles from './DeveloperPlatformLayout.module.scss'

interface SearchOption {
  value: string
  label: string
  path: string
}

/**
 * Top header: breadcrumb (Workspace / current page, or a page-provided
 * trail), global search over the workspace pages, and the light/dark mode
 * toggle.
 */
export function WorkspaceHeader() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { mode, toggleMode } = useThemeMode()
  const override = useBreadcrumbOverride()
  const [query, setQuery] = useState('')

  const current = resolveWorkspaceLocation(location.pathname)

  const crumbs = useMemo<BreadcrumbItem[]>(() => {
    if (override) return override
    if (current.subPage) {
      return [
        { label: t(current.page.labelKey), href: workspacePath(current.page) },
        { label: subPageLabel(current.subPage, t) }
      ]
    }
    return [
      { label: t('developerPlatform.workspace'), href: workspacePath('dashboard') },
      { label: t(current.page.labelKey) }
    ]
  }, [override, current.page, current.subPage, t])

  const allOptions = useMemo<SearchOption[]>(
    () =>
      WORKSPACE_PAGES.flatMap(page => [
        { value: page.id, label: t(page.labelKey), path: workspacePath(page) },
        ...(page.children ?? []).map(child => ({
          value: child.id,
          label: `${t(page.labelKey)} · ${subPageLabel(child, t)}`,
          path: subPagePath(page, child)
        }))
      ]),
    [t]
  )

  const options = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return allOptions
      .filter(({ label }) => !needle || label.toLowerCase().includes(needle))
      .map(({ value, label }) => ({ value, label }))
  }, [query, allOptions])

  const isDark = mode === 'dark'

  return (
    <div className={styles.header} data-testid="dp-header">
      <Breadcrumb
        className={styles.breadcrumb}
        aria-label={t('developerPlatform.breadcrumb')}
        items={crumbs.map((item, index) =>
          index === crumbs.length - 1 ? { ...item, 'data-testid': 'dp-breadcrumb-page' } : item
        )}
        data-testid="dp-breadcrumb"
      />
      <div className={styles.headerActions}>
        <AutoComplete
          className={styles.search}
          options={options}
          value={query}
          onChange={setQuery}
          onSelect={(value: string) => {
            setQuery('')
            const option = allOptions.find(o => o.value === value)
            if (option) navigate(option.path)
          }}
        >
          <Input
            allowClear
            prefix={<Icon icon="Search" size={16} />}
            placeholder={t('developerPlatform.searchPlaceholder')}
            data-testid="dp-search"
          />
        </AutoComplete>
        <Button
          type="text"
          className={styles.modeToggle}
          onClick={toggleMode}
          aria-pressed={isDark}
          aria-label={t('developerPlatform.mode.toggle')}
          title={isDark ? t('developerPlatform.mode.light') : t('developerPlatform.mode.dark')}
          data-testid="dp-mode-toggle"
        >
          <span className={styles.modeIcon} aria-hidden="true">
            <Icon icon={isDark ? 'Moon' : 'Sun'} size={18} />
          </span>
          {t('developerPlatform.mode.label')}
        </Button>
      </div>
    </div>
  )
}
